/**
 * Global search router — multi-signal fitment routing ONLY.
 *
 * Intercepts submit on search forms. If the typed query contains BOTH a year
 * (19XX/20XX) AND a machine word ("backhoe", "skid steer", ...), the intent is
 * unambiguous fitment browsing ("2016 backhoe") and we redirect to
 * /pages/fitment-search with structured params. EVERYTHING else — part
 * numbers, model names, free text — submits natively to /search.
 *
 * There is deliberately NO single-token classification here. The old
 * letters+digits "isModel" heuristic (and its 2026-05-27 tightened variant)
 * hijacked part-number searches to the fitment page, which has no data for
 * them — 53% of live PNs matched the loose rule, and no regex can separate a
 * PN from a model (5,387 of 6,831 real models share the shape). Model search
 * lives where it is called out explicitly: the homepage machine tab, the
 * fitment page's picker and category tiles, My Fleet, and the PDP fit badge —
 * all of which pass structured params and never hit this code.
 * Full background: search-routing-fix-plan.md (repo root).
 *
 * DO NOT add token-shape guessing back into this file. If bare-model search
 * demand ever proves real (see plan §B3), the answer is the verified
 * model-index design in plan §C, not a regex.
 */
(function () {
  const YEAR = /^(19|20)\d{2}$/;

  // Machine categories the fitment backend actually has data for. Subsystem
  // nouns (engine, transmission, axle) are intentionally absent — customers
  // type them as product modifiers ("transmission oil"), and the backend has
  // no such machine data (commit cd4f6bb).
  const MACHINE_ALIASES = new Map([
    ['backhoe','Backhoe'], ['skid steer','Skid Steer'], ['skidsteer','Skid Steer'],
    ['telehandler','Telehandler'], ['loader','Loader'], ['dozer','Dozer'],
    ['cotton picker','Cotton Picker'], ['combine','Combine'], ['harvester','Harvester'],
    ['excavator','Excavator'], ['excav','Excavator'], ['forklift','Forklift'],
    ['trencher','Trencher'], ['baler','Baler'], ['sprayer','Sprayer'],
    ['compactor','Compactor'], ['roller','Roller'], ['mower','Mower'],
    ['tractor','Tractor']
  ]);

  function tokenize(raw) {
    const tokens = (raw || '').trim().split(/\s+/).filter(Boolean);
    const years = [], machines = [], rest = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      const next = tokens[i + 1];
      if (next) {
        const two = MACHINE_ALIASES.get((t + ' ' + next).toLowerCase());
        if (two) { machines.push(two); i++; continue; }
      }
      const one = MACHINE_ALIASES.get(t.toLowerCase());
      if (one) { machines.push(one); continue; }
      if (YEAR.test(t)) years.push(t);
      else              rest.push(t);
    }
    return { years, machines, freeText: rest.join(' ') };
  }

  function interceptForm(form) {
    if (form._fsRouterBound) return;
    form._fsRouterBound = true;

    // Forms that opt out (e.g. the hero's "By part" tab) always submit
    // straight to /search. With multi-signal-only routing the attribute is
    // nearly moot, but honoring it keeps those surfaces guaranteed-native.
    if (form.hasAttribute('data-no-fitment-router')) return;

    form.addEventListener('submit', (e) => {
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const raw = input.value.trim();
      if (!raw) return;

      const tokens = tokenize(raw);
      // Route ONLY on year + machine together. Either alone is ambiguous
      // (a bare year is often a part-number fragment; a bare machine word is
      // often a product modifier) — native search handles those better, and
      // the /search empty state links to fitment search for the rest.
      if (tokens.years.length === 0 || tokens.machines.length === 0) {
        return; // native submit proceeds untouched
      }

      e.preventDefault();
      const qs = new URLSearchParams();
      qs.set('year',    tokens.years[0]);
      qs.set('machine', tokens.machines[0]);
      if (tokens.freeText) qs.set('q', tokens.freeText);
      window.location.href = '/pages/fitment-search?' + qs.toString();
    });
  }

  function scan() {
    document.querySelectorAll('form[action$="/search"], form.predictive-search-form, .search-page-input__parent').forEach(interceptForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  // Some themes lazy-mount search modals — catch late additions.
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
